import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, KeyRound, Check, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { adminChangePassword } from "@/lib/blogApi";

const PASS_MIN = 8;

const strength = (pw) => {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 5);
};
const STRENGTH_LABEL = ["Too short", "Weak", "Okay", "Good", "Strong", "Excellent"];

const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset state when closed
      setTimeout(() => {
        setCurrent(""); setNext(""); setConfirm("");
        setShowCurrent(false); setShowNext(false);
        setDone(false); setLoading(false);
      }, 200);
      return;
    }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const pwStrength = strength(next);
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit =
    current.length > 0 &&
    next.length >= PASS_MIN &&
    next === confirm &&
    next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await adminChangePassword(current, next);
      setDone(true);
      toast.success("Password updated. Session refreshed.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not update password.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-brand-black/70 z-[80] flex items-center justify-center p-4 md:p-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
      data-testid="change-password-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white max-w-md w-full p-8 md:p-10 border border-brand-green/15 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="change-password-close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-brand-black/60 hover:text-brand-black"
        >
          <X size={18} />
        </button>

        {!done ? (
          <>
            <div className="w-12 h-12 bg-brand-green text-brand-cream flex items-center justify-center mb-5">
              <KeyRound size={18} />
            </div>
            <p className="eyebrow mb-2">◦  Admin Settings</p>
            <h2 className="font-display text-3xl text-brand-black leading-tight">Change your password.</h2>
            <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">
              New password must be at least {PASS_MIN} characters. The new password takes effect immediately. Your current session stays signed in.
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <PasswordField
                label="Current Password"
                value={current}
                onChange={setCurrent}
                show={showCurrent}
                onToggle={() => setShowCurrent(!showCurrent)}
                testid="cp-current"
                autoFocus
              />
              <PasswordField
                label="New Password"
                value={next}
                onChange={setNext}
                show={showNext}
                onToggle={() => setShowNext(!showNext)}
                testid="cp-new"
              />
              {next.length > 0 && (
                <div data-testid="cp-strength">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">Strength</span>
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${pwStrength >= 4 ? "text-brand-green" : pwStrength >= 2 ? "text-brand-teal" : "text-red-700"}`}>
                      {STRENGTH_LABEL[pwStrength] || ""}
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span
                        key={i}
                        className={`h-1 ${i < pwStrength ? (pwStrength >= 4 ? "bg-brand-green" : pwStrength >= 2 ? "bg-brand-teal" : "bg-red-600") : "bg-brand-black/10"}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              <PasswordField
                label="Confirm New Password"
                value={confirm}
                onChange={setConfirm}
                show={showNext}
                onToggle={() => setShowNext(!showNext)}
                testid="cp-confirm"
                error={mismatch ? "Passwords do not match" : null}
              />
              {current && next && current === next && (
                <p className="text-xs text-red-700" data-testid="cp-same">New password must be different from the current one.</p>
              )}

              <button
                type="submit"
                disabled={!canSubmit || loading}
                data-testid="cp-submit"
                className="w-full btn-primary disabled:opacity-50"
              >
                {loading ? <><Loader2 size={14} className="animate-spin" /> Updating</> : <><KeyRound size={14} /> Update Password</>}
              </button>

              <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-brand-black/45 pt-2">
                <ShieldCheck size={11} /> Bcrypt-hashed · Stored only in MongoDB
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-green text-brand-cream mb-5">
              <Check size={22} />
            </div>
            <h2 className="font-display text-3xl text-brand-black">Password updated.</h2>
            <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">
              Your new password takes effect immediately. Future logins must use the new password.
            </p>
            <button onClick={onClose} className="btn-primary mt-7">Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

const PasswordField = ({ label, value, onChange, show, onToggle, testid, autoFocus = false, error = null }) => (
  <div>
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{label}</span>
    <div className="mt-1 relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`w-full pl-4 pr-11 py-3 bg-brand-cream/40 border focus:outline-none text-sm ${error ? "border-red-600/50 focus:border-red-700" : "border-brand-green/20 focus:border-brand-teal"}`}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-brand-black/45 hover:text-brand-black"
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
    {error && <p className="text-xs text-red-700 mt-1.5">{error}</p>}
  </div>
);

export default ChangePasswordModal;
